import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, Tag } from "antd";
import Collapse, { Panel } from "../../../../common/component/Collapse";
import {
  DatePickerComponent,
  FormWrapperInputComponent,
  HeaderComponent
} from "../../../../common/component/Index";
import Wrapper from "../../../../common/component/Wrapper";
import Input from "../../../../common/component/Input";
import {
  DETAIL_ACTIONS,
  CAMPAIGN as CAMPAIGN_ENUM,
  DATE_TIME_FORMAT,
  MARKUP
} from "../../../../../../constants/Define";
import { capitalizeFirstLetter } from "../../../../../../helpers/functions";
import AntSelect, { AntOption } from "../../../../common/component/AntSelect";
import { useTranslation } from "react-i18next";
import { cloneDeep, get, set, toArray, isEqual } from "lodash";
import classNames from "classnames";
import moment from "moment";
import { formatPercentValue } from "../../../../../../utils/commonUtils";
import Switch from "containers/backend/common/component/Switch";
import * as FnbService from "../../../../../../services/fnbService";

const CheckboxGroup = Checkbox.Group;

const defaultCollapseKey = "campaignInfo";

function CampaignInfoSection(props) {
  const {
    createType,
    campaign,
    validation,
    action,
    campaignState,
    onChange,
    setCampaign,
    renderInfoTooltip,
    getTagsByGroup,
    tagsByGroup
  } = props;
  const { channels } = campaignState;
  const { t } = useTranslation();
  const [collapseKey, setCollapseKey] = useState(defaultCollapseKey);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [fnbProperties, setFnbProperties] = useState([]);
  const [isLoadingFnbProperties, setIsLoadingFnbProperties] = useState(false);
  const isFnbCreateType = createType === CAMPAIGN_ENUM.CREATE_TYPE.FNB;

  const disabledDate = current => {
    return current < moment().startOf("day");
  };

  const propertyTypes = [
    {
      value: false,
      label: 'Hotel',
    }, {
      value: true,
      label: 'Homestay',
    },
  ]

  const onSelectScheduleDate = data => {
    const startDate = cloneDeep(data.fullValue[0]);
    const endDate = cloneDeep(data.fullValue[1]);
    set(campaign, "schedules[0].startDate", startDate);
    set(campaign, "schedules[0].endDate", endDate);
    onChange("schedules", campaign.schedules);
  };

  const getProductTypeEnums = (includeAllOption = false, selectTypes = []) => {
    if (isFnbCreateType) {
      return toArray(CAMPAIGN_ENUM.PRODUCT_TYPE).filter(
        item => item.id === CAMPAIGN_ENUM.PRODUCT_TYPE.FNB.id
      );
    }

    return toArray(CAMPAIGN_ENUM.PRODUCT_TYPE).filter(
      item =>
        item.id !== CAMPAIGN_ENUM.PRODUCT_TYPE.HOTEL.id &&
        item.id !== CAMPAIGN_ENUM.PRODUCT_TYPE.FNB.id &&
        (!selectTypes?.length || selectTypes.indexOf(item.value) >= 0) &&
        (includeAllOption || item.id !== CAMPAIGN_ENUM.PRODUCT_TYPE.SKU.id)
    );
  };

  const onChangeProductType = list => {
    if (isFnbCreateType) {
      setCampaign(Object.assign({}, campaign, { productType: [CAMPAIGN_ENUM.PRODUCT_TYPE.FNB.value] }));
      return;
    }

    const getSelectedValue = item => item.value;
    const allOptions = getProductTypeEnums().map(getSelectedValue);
    const productType =
      list.indexOf(CAMPAIGN_ENUM.PRODUCT_TYPE.SKU.value) >= 0 ||
        isEqual(allOptions, list)
        ? getProductTypeEnums(true).map(getSelectedValue)
        : list;
    const newData = Object.assign({}, campaign, { productType });
    setCampaign(newData);
  };

  const onChangePropertyIds = propertyIds => {
    setCampaign({
      ...campaign,
      propertyIds,
      outletIds: [],
      menuIds: [],
      menuItemIds: [],
    });
  };

  const onToggleCollapse = () => {
    if (collapseKey) {
      // close
      setCollapseKey();
    } else {
      // open
      setCollapseKey(defaultCollapseKey);
    }
  };

  const onChangeDiscountValue = (value, type) => {
    type = type || campaign.discountType;
    return onChange(
      "discountValue",
      formatPercentValue(
        value,
        type === CAMPAIGN_ENUM.DISCOUNT_TYPE.PERCENT.id ? 100 : null
      )
    );
  }

  const onChangeActive = () => {
    setCampaign({
      ...campaign,
      isActive: !isActive,
    })
    setIsActive(!isActive);
  };

  useEffect(() => {
    if (campaign.isActive !== undefined) {
      setIsActive(campaign.isActive);
    }
  }, [campaign.isActive]);

  useEffect(() => {
    const hasError = validation && (validation.name || validation.productType || validation.schedules || validation.channel || validation.platforms)
    if (hasError && !collapseKey) {
      setCollapseKey(defaultCollapseKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation])

  useEffect(() => {
    getTagsByGroup({ tag_group_code: "voucher_tag" });
  }, [])

  useEffect(() => {
    if (!isFnbCreateType) return;

    let isMounted = true;
    setIsLoadingFnbProperties(true);
    FnbService.getFrontendProperties()
      .then(properties => {
        if (isMounted) setFnbProperties(properties || []);
      })
      .catch(() => {
        if (isMounted) setFnbProperties([]);
      })
      .finally(() => {
        if (isMounted) setIsLoadingFnbProperties(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isFnbCreateType])

  useEffect(() => {
    const tag = (tagsByGroup || []).find(x => x.id === campaign.tagId);
    setSelectedTag(tag)
  }, [campaign.tagId, tagsByGroup])

  return (
    <Collapse
      activeKey={collapseKey}
      bordered={false}
      defaultActiveKey={defaultCollapseKey}
      style={{ width: "100%", background: "inherit" }}
      expandIconPosition={"right"}
      onChange={onToggleCollapse}
    >
      <Panel
        key={defaultCollapseKey}
        header={
          <HeaderComponent
            title={t("item.campaign.headingForm.info")}
            className="col-md-12"
            style={{
              userSelect: "none",
              paddingTop: "unset",
              paddingBottom: "unset"
            }}
          />
        }
      >
        <Wrapper className={"row campaign-collapse-row"}>
          <Wrapper className="col-md-6">
            <FormWrapperInputComponent
              isRequired={true}
              isValidation={true}
              dataValidation={campaign.name}
              validation={validation.name}
              labelTitle={t(`item.campaign.name`)}
              visible={action !== DETAIL_ACTIONS.TRANSLATE}
            >
              <Input
                type="text"
                autoComplete="off"
                placeholder={capitalizeFirstLetter(
                  (
                    t("placeHolderForm.input") +
                    " " +
                    t("item.campaign.name")
                  ).toLowerCase()
                )}
                className="form-control"
                name="name"
                onChange={e => onChange("name", e.target.value)}
                value={campaign.name ? campaign.name : ""}
              />
            </FormWrapperInputComponent>
          </Wrapper>
          <Wrapper className="col-md-6">
            {createType !== CAMPAIGN_ENUM.CREATE_TYPE.HOTEL &&
              createType !== CAMPAIGN_ENUM.CREATE_TYPE.MARKUP && (
                <FormWrapperInputComponent
                  isRequired={true}
                  isValidation={true}
                  dataValidation={campaign.productType}
                  validation={validation.productType}
                  labelTitle={
                    <>
                      {t(`item.campaign.productType`)}
                      {!isFnbCreateType && (
                        <Checkbox
                          className="ml-2"
                          onChange={e =>
                            onChangeProductType(
                              e.target.checked
                                ? [CAMPAIGN_ENUM.PRODUCT_TYPE.SKU.value]
                                : []
                            )
                          }
                          checked={
                            (campaign.productType || []).indexOf(
                              CAMPAIGN_ENUM.PRODUCT_TYPE.SKU.value
                            ) !== -1
                          }
                          disabled={
                            createType === CAMPAIGN_ENUM.CREATE_TYPE.DIEM
                          }
                        >
                          {t("action.all")}
                        </Checkbox>
                      )}
                    </>
                  }
                  visible={action !== DETAIL_ACTIONS.TRANSLATE}
                >
                  <div>
                    <CheckboxGroup
                      className="m-t"
                      options={getProductTypeEnums().map(item => ({
                        label: item.label,
                        value: item.value,
                        disabled: createType === CAMPAIGN_ENUM.CREATE_TYPE.DIEM && item.key === 'tour'
                      }))}
                        value={campaign.productType}
                        onChange={onChangeProductType}
                    />
                  </div>
                </FormWrapperInputComponent>
              )}
            {(createType === CAMPAIGN_ENUM.CREATE_TYPE.HOTEL) &&(
              <FormWrapperInputComponent
                isRequired={true}
                validation={validation.isCampaignStaynFun}
                labelTitle={t(`item.campaign.isCampaignStaynFun`)}
                visible={action !== DETAIL_ACTIONS.TRANSLATE}
              >
                <AntSelect
                  showArrow
                  placeholder={capitalizeFirstLetter(
                    (
                      t("placeHolderForm.select") +
                      " " +
                      t("item.campaign.isCampaignStaynFun")
                    ).toLowerCase()
                  )}
                  value={campaign.isCampaignStaynFun}
                  onChange={e => onChange("isCampaignStaynFun", e)}
                  style={{
                    width: "100%"
                  }}
                  disabled={action !== DETAIL_ACTIONS.CREATE}
                >
                  {propertyTypes.map((item, index) => (
                    <AntOption key={index} value={item.value}>
                      {item.label}
                    </AntOption>
                  ))}
                </AntSelect>
              </FormWrapperInputComponent>
            )}
            {createType === CAMPAIGN_ENUM.CREATE_TYPE.MARKUP && (
              <FormWrapperInputComponent
                isRequired={true}
                isValidation={true}
                dataValidation={campaign.productType}
                validation={validation.productType}
                labelTitle={t(`item.campaign.productType`)}
                visible={action !== DETAIL_ACTIONS.TRANSLATE}
              >
                <div>
                  <AntSelect
                    className="select-types"
                    showArrow
                    placeholder={capitalizeFirstLetter(
                      (
                        t("placeHolderForm.select") +
                        " " +
                        t("item.campaign.productType")
                      ).toLowerCase()
                    )}
                    value={campaign.productType[0]}
                    onChange={productType =>
                      onChange("productType", [productType])
                    }
                    style={{
                      width: "100%"
                    }}
                  >
                    {toArray(MARKUP.CAMPAIGN_TYPE).map(mk => (
                      <AntOption key={mk.key} value={mk.id}>
                        {mk.label}
                      </AntOption>
                    ))}
                  </AntSelect>
                </div>
              </FormWrapperInputComponent>
            )}
          </Wrapper>
          {isFnbCreateType && (
            <Wrapper className="col-md-6">
              <FormWrapperInputComponent
                isRequired={true}
                isValidation={true}
                dataValidation={campaign.propertyIds}
                validation={validation.propertyIds}
                messageBelowInput={true}
                labelTitle="Property"
                visible={action !== DETAIL_ACTIONS.TRANSLATE}
              >
                <AntSelect
                  className="select-types"
                  showArrow
                  showSearch
                  mode="multiple"
                  loading={isLoadingFnbProperties}
                  placeholder="Select Property"
                  value={campaign.propertyIds}
                  onChange={onChangePropertyIds}
                  style={{ width: "100%" }}
                  filterOption={(input, option) => {
                    const optionText = String(option.props?.children || '').toLowerCase();
                    return optionText.indexOf(input.toLowerCase()) >= 0;
                  }}
                >
                  {fnbProperties.map(property => (
                    <AntOption key={property.id} value={property.id}>
                      {property.name}
                    </AntOption>
                  ))}
                </AntSelect>
              </FormWrapperInputComponent>
            </Wrapper>
          )}
          <Wrapper className="col-md-6">
            <FormWrapperInputComponent
              labelTitle={t(`item.campaign.description`)}
              visible={action !== DETAIL_ACTIONS.TRANSLATE}
            >
              <Input
                type="text"
                autoComplete="off"
                placeholder={capitalizeFirstLetter(
                  (
                    t("placeHolderForm.input") +
                    " " +
                    t("item.campaign.description")
                  ).toLowerCase()
                )}
                className="form-control"
                name="description"
                onChange={e => onChange("description", e.target.value)}
                value={campaign.description ? campaign.description : ""}
              />
            </FormWrapperInputComponent>
          </Wrapper>
          <Wrapper className="col-md-6">
            <FormWrapperInputComponent
              isRequired={true}
              isValidation={true}
              dataValidation={campaign.schedules}
              validation={validation.schedules}
              labelTitle={t(`item.campaign.schedules`)}
              visible={action !== DETAIL_ACTIONS.TRANSLATE}
            >
              <DatePickerComponent
                className={classNames("form-control applyDates w100-percent", {
                  "border-red": !!validation.schedules
                })}
                disabledDate={disabledDate}
                hmsComponent={`RangePicker`}
                format={DATE_TIME_FORMAT.DATE_TIME}
                showTime={{
                  format: DATE_TIME_FORMAT.SHORT_TIME
                }}
                style={{
                  padding: 0,
                  borderWidth: validation.schedules ? 1 : 0
                }}
                onChange={onSelectScheduleDate}
                name={`applyDates`}
                placeholder={[
                  t("placeHolderForm.startDate"),
                  t("placeHolderForm.endDate")
                ]}
                value={[
                  get(campaign, "schedules[0].startDate"),
                  get(campaign, "schedules[0].endDate")
                ]}
              />
            </FormWrapperInputComponent>
          </Wrapper>
          {/*<Wrapper className="col-md-6">*/}
          {/*    <FormWrapperInputComponent*/}
          {/*        messageBelowInput={ true }*/}
          {/*        labelTitle={ t(`item.campaign.countries`) }*/}
          {/*    >*/}
          {/*        <AntSelect className="select-types"*/}
          {/*                   showArrow*/}
          {/*                   mode="multiple"*/}
          {/*                   value={ campaign.countries }*/}
          {/*                   onChange={ country => onChange('countries', country) }*/}
          {/*                   style={ { width: '100%' } }*/}
          {/*        >*/}
          {/*            { countries.map(country => (*/}
          {/*                <AntOption key={ country.code } value={ country.code }>*/}
          {/*                    { country.name }*/}
          {/*                </AntOption>*/}
          {/*            )) }*/}
          {/*        </AntSelect>*/}
          {/*    </FormWrapperInputComponent>*/}
          {/*</Wrapper>*/}
          <Wrapper className="col-md-6">
            <FormWrapperInputComponent
              isRequired={true}
              isValidation={true}
              dataValidation={campaign.channelNull}
              validation={validation.channelNull}
              messageBelowInput={true}
              labelTitle={
                t(`item.campaign.channel`)
              }
              visible={action !== DETAIL_ACTIONS.TRANSLATE}
            >
              <AntSelect
                className="select-types"
                showArrow
                mode="multiple"
                placeholder={capitalizeFirstLetter(
                  (
                    t("placeHolderForm.select") +
                    " " +
                    t("item.campaign.channel")
                  ).toLowerCase()
                )}
                value={campaign.channel}
                onChange={channel => onChange("channel", channel)}
                style={{ width: "100%" }}
              >
                {channels.map(channel => (
                  <AntOption
                    key={channel.code}
                    value={channel.code}
                  >
                    {channel.name}
                  </AntOption>
                ))}
              </AntSelect>
            </FormWrapperInputComponent>
          </Wrapper>
          <Wrapper className="col-md-6">
            <FormWrapperInputComponent
              isRequired={false}
              isValidation={true}
              dataValidation={campaign.platforms}
              validation={validation.platforms}
              messageBelowInput={true}
              labelTitle={t(`item.campaign.platforms`)}
              visible={action !== DETAIL_ACTIONS.TRANSLATE}
            >
              <AntSelect
                className="select-types"
                showArrow
                mode="multiple"
                placeholder={capitalizeFirstLetter(
                  (
                    t("placeHolderForm.select") +
                    " " +
                    t("item.campaign.platforms")
                  ).toLowerCase()
                )}
                value={campaign.platforms}
                onChange={platforms => onChange("platforms", platforms)}
                style={{ width: "100%" }}
              >
                {toArray(CAMPAIGN_ENUM.PLATFORMS).map(platform => (
                  <AntOption key={platform.value} value={platform.value}>
                    {platform.label}
                  </AntOption>
                ))}
              </AntSelect>
            </FormWrapperInputComponent>
          </Wrapper>

          <Wrapper className="col-md-6">
            {createType !== CAMPAIGN_ENUM.CREATE_TYPE.DIEM ?
              <FormWrapperInputComponent
                isRequired={false}
                isValidation={false}
                dataValidation={campaign.tagId}
                validation={validation.tagId}
                messageBelowInput={true}
                labelTitle={
                  t(`item.campaign.tag`)
                }
                visible={action !== DETAIL_ACTIONS.TRANSLATE}
              >
                <div className="d-flex align-items-center">
                  <AntSelect
                    className="select-types"
                    showArrow
                    allowClear
                    placeholder={capitalizeFirstLetter(
                      (
                        t("placeHolderForm.select") +
                        " " +
                        t("item.campaign.tag")
                      ).toLowerCase()
                    )}
                    value={campaign.tagId}
                    onChange={tagId => onChange("tagId", tagId)}
                    style={{ width: "50%" }}
                  >
                    {tagsByGroup.map(tag => (
                      <AntOption key={tag.id} value={tag.id}>
                        {tag.name}
                      </AntOption>
                    ))}
                  </AntSelect>

                  {
                    !!selectedTag && (
                      <Tag className="ml-2 d-flex align-items-center" style={{ height: 38 }} color={selectedTag.bgColor}>
                        <span style={{ color: selectedTag.color }}>{selectedTag.name}</span>
                      </Tag>
                    )
                  }
                </div>
              </FormWrapperInputComponent>
              :
              <Wrapper className="row">
                <Wrapper className="col-md-6">
                  <FormWrapperInputComponent
                    isRequired={true}
                    isValidation={true}
                    dataValidation={campaign.discountType}
                    validation={validation.discountType}
                    messageBelowInput={true}
                    className="with-fix with-currency-suffix"
                    labelTitle={t(`item.campaign.discountPointType`)}
                    visible={action !== DETAIL_ACTIONS.TRANSLATE}
                  >
                    <AntSelect
                      className="select-types"
                      showArrow
                      placeholder={capitalizeFirstLetter(
                        (
                          t("placeHolderForm.select") +
                          " " +
                          t("item.campaign.discountType")
                        ).toLowerCase()
                      )}
                      value={campaign.discountType}
                      onChange={type =>
                      {
                        onChangeDiscountValue(campaign.discountValue, type);
                        return onChange("discountType", type);
                      }}
                      style={{ width: "100%" }}
                    >
                      {toArray(CAMPAIGN_ENUM.DISCOUNT_TYPE).map(
                        item =>
                          !item.hide && (
                            <AntOption
                              key={item.key}
                              value={item.id}
                              disabled={item.disabled || item.id == 'PERCENTAGE'}
                            >
                              {t(`item.campaign.discountTypes.${item.key}`)}
                            </AntOption>
                          )
                      )}
                    </AntSelect>
                  </FormWrapperInputComponent>
                </Wrapper>
                <Wrapper className="col-md-6">
                  <FormWrapperInputComponent
                    isRequired={true}
                    isValidation={true}
                    dataValidation={campaign.discountValue}
                    validation={validation.discountValue}
                    messageBelowInput={true}
                    className="gift-point"
                    labelTitle={t(`item.campaign.discountPointValue`)}
                    visible={action !== DETAIL_ACTIONS.TRANSLATE}
                  >
                    <Input
                      type="currency"
                      autoComplete="off"
                      placeholder={capitalizeFirstLetter(
                        (
                          t("placeHolderForm.input") +
                          " " +
                          t("item.campaign.discountPointValue")
                        ).toLowerCase()
                      )}
                      className="form-control gift-point"
                      name="discountValue"
                      onChange={e => onChangeDiscountValue(e.target.value)}
                      value={campaign.discountValue}
                    />
                  </FormWrapperInputComponent>
                </Wrapper>
              </Wrapper>
            }
          </Wrapper>

          {createType === CAMPAIGN_ENUM.CREATE_TYPE.DIEM && 
            <Wrapper className="col-md-6">
              <span className="active-detail-switch">
                <Switch
                  className="mr-4"
                  checked={isActive}
                  onChange={() => onChangeActive()}
                />
              </span>
              <span style={{marginLeft: '-5px'}}>
                {isActive == true ? (t("item.campaign.activePoint")) : t("item.campaign.deactivePoint")}
              </span>
            </Wrapper>
          }

        </Wrapper>
      </Panel>
    </Collapse>
  );
}

CampaignInfoSection.propTypes = {
  createType: PropTypes.object,
  campaign: PropTypes.object,
  validation: PropTypes.object,
  action: PropTypes.string,
  campaignState: PropTypes.object,
  onChange: PropTypes.func,
  setCampaign: PropTypes.func,
  renderInfoTooltip: PropTypes.func
};

CampaignInfoSection.defaultProps = {
  createType: "",
  campaign: {},
  validation: {},
  action: "",
  campaignState: {},
  onChange: () => { },
  setCampaign: () => { },
  renderInfoTooltip: () => { }
};

export default CampaignInfoSection;
